import { type Allergen } from '@owner/menu-items'

import { type ResourceTypes } from '../types'

interface InputTranslation {
  label: string
  description?: string
}

interface BooleanFilterTranslation {
  label: string
  options: {
    true: string
    false: string
  }
}

interface DropdownFilterTranslation {
  label: string
  options: Record<string, string>
}

export interface MenuTranslations {
  allergen: Record<Allergen, string>
  filters: Record<string, BooleanFilterTranslation | DropdownFilterTranslation>
  resources: Record<ResourceTypes, string>
  selectors: Record<string, string>
  inputs: Record<string, InputTranslation>
  labels: Record<string, string>
  tooltips: Record<string, string>
  errors: Record<string, string>
}

export const menuEn: MenuTranslations = {
  allergen: {
    dairy: 'Dairy',
    eggs: 'Eggs',
    fish: 'Fish',
    gluten: 'Gluten',
    mango: 'Mango',
    peanuts: 'Peanuts',
    sesame: 'Sesame',
    shellfish: 'Shellfish',
    soy: 'Soy',
    'tree-nuts': 'Tree Nuts',
  },
  errors: {
    save: 'Unable to save {resourceName}.',
  },
  filters: {
    alcohol: {
      label: 'Alcohol',
      options: {
        false: 'No Alcohol',
        true: 'Alcohol',
      },
    },
    allowInstructions: {
      label: 'Allow Instructions',
      options: {
        false: 'No Instructions',
        true: 'Instructions',
      },
    },
    available: {
      label: 'Availability',
      options: {
        false: 'Not Available',
        true: 'Available',
      },
    },
    campaign: {
      label: 'Associated with automation',
      options: {
        basic: 'Basic',
        welcome: 'Welcome',
        winback: 'Winback',
      },
    },
    glutenFree: {
      label: 'Gluten Free',
      options: {
        false: 'Not Gluten Free',
        true: 'Gluten Free',
      },
    },
    hasCategories: {
      label: 'Categories',
      options: {
        false: 'No Categories',
        true: 'Has Categories',
      },
    },
    hasItems: {
      label: 'Items',
      options: {
        false: 'No Items',
        true: 'Has Items',
      },
    },
    hasModifierSets: {
      label: 'Modifier Sets',
      options: {
        false: 'No Modifier Sets',
        true: 'Has Modifier Sets',
      },
    },
    hasModifiers: {
      label: 'Modifiers',
      options: {
        false: 'No Modifiers',
        true: 'Has Modifiers',
      },
    },
    hasPreselectedModifiers: {
      label: 'Preselected Modifiers',
      options: {
        false: 'No Preselected Modifiers',
        true: 'Has Preselected Modifiers',
      },
    },
    hasUpsellCategories: {
      label: 'Upsell Categories',
      options: {
        false: 'No Upsell Categories',
        true: 'Has Upsell Categories',
      },
    },
    linkedToPos: {
      label: 'Linked to POS',
      options: {
        false: 'Not linked',
        true: 'Linked',
      },
    },
    popular: {
      label: 'Popular',
      options: {
        false: 'Not Popular',
        true: 'Popular',
      },
    },
    promotional: {
      label: 'Promotional',
      options: {
        false: 'Not Promotional',
        true: 'Promotional',
      },
    },
    taxable: {
      label: 'Tax',
      options: {
        false: 'Not Taxable',
        true: 'Taxable',
      },
    },
    topping: {
      label: 'Topping',
      options: {
        false: 'Not Topping',
        true: 'Topping',
      },
    },
    toppings: {
      label: 'Toppings',
      options: {
        false: 'Not Toppings',
        true: 'Toppings',
      },
    },
    type: {
      label: 'Menu Type',
      options: {
        catering: 'Catering',
        olo: 'Online Ordering',
      },
    },
    upsell: {
      label: 'Upsell',
      options: {
        false: 'Not Upsell Item',
        true: 'Is Upsell Item',
      },
    },
    vegetarian: {
      label: 'Vegetarian',
      options: {
        false: 'Not Vegetarian',
        true: 'Vegetarian',
      },
    },
  },
  inputs: {
    alcohol: {
      label: 'Alcohol',
    },
    allergens: {
      label: 'Allergens',
    },
    amount: {
      description: "Overridden by the '{variationGroupName}' variation group",
      label: 'Amount',
    },
    description: {
      label: 'Description',
    },
    displayName: {
      description: 'This is the name your customers will see.',
      label: 'Display Name',
    },
    glutenFree: {
      label: 'Gluten Free',
    },
    image: {
      label: 'Photo',
    },
    name: {
      label: 'Internal Name',
    },
    taxable: {
      label: 'Apply sales tax for this item',
    },
    vegetarian: {
      label: 'Vegetarian',
    },
  },
  labels: {
    'custom-internal': 'Use a different name internally',
    'custom-internal-description':
      'This is the name you and your staff will see on the dashboard, tablet and printed tickets.',
    'more-options': 'More Options',
    options: 'Options',
    'resource-created': '{resourceName} Created',
    'resource-updated': '{resourceName} Updated',
  },
  resources: {
    category: 'Category | Categories',
    coupon: 'Coupon | Coupons',
    item: 'Item | Items',
    menu: 'Menu | Menus',
    modifier: 'Modifier | Modifiers',
    'modifier-set': 'Modifier Group | Modifier Groups',
  },
  selectors: {
    loading: 'Loading...',
  },
  tooltips: {
    delete: 'Delete',
    duplicate: 'Duplicate',
  },
}
